import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { dateTime, humanise } from '../lib/format';
import { Button, Card, CardHead, Empty, Field, Modal, Notice, Select, useToast } from '../components/ui';

interface PendingCategory {
  id: string;
  name: string;
  slug: string;
  path: string;
  sector: string;
  parentId: string | null;
  source: string;
  createdBy: string | null;
  createdAt: string;
}

interface CategoryOption {
  id: string;
  name: string;
  path: string;
}

/**
 * FR-SRCH-07's "Others" auto-append, gated by FR-SRCH-08.
 *
 * TR-5 is the risk this screen exists to manage: the source SRD says a typed
 * name becomes visible to every user immediately, which makes one abusive or
 * misspelled entry everybody's problem with no way back. Merging into an
 * existing category is offered alongside approve/reject because a near
 * duplicate is the common case, not the exception.
 */
export function CategoryModerationPage() {
  const [merging, setMerging] = useState<PendingCategory | null>(null);
  const [mergeInto, setMergeInto] = useState('');
  const toast = useToast();
  const qc = useQueryClient();

  const pending = useQuery({
    queryKey: ['categories-pending'],
    queryFn: () => api.get<PendingCategory[]>('/categories/moderation/pending'),
  });

  const all = useQuery({
    queryKey: ['categories-all'],
    queryFn: () => api.get<CategoryOption[]>('/categories?tree=true'),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['categories-pending'] });
    qc.invalidateQueries({ queryKey: ['dashboard-badges'] });
  };

  const moderate = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'APPROVED' | 'REJECTED' }) =>
      api.patch(`/categories/${id}/moderation/${status}`),
    onSuccess: (_d, v) => {
      toast(v.status === 'APPROVED' ? 'Category published' : 'Request rejected', 'success');
      invalidate();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const merge = useMutation({
    mutationFn: ({ id, intoId }: { id: string; intoId: string }) => api.post(`/categories/${id}/merge`, { intoId }),
    onSuccess: () => {
      toast('Merged — listings and providers moved across', 'success');
      setMerging(null);
      setMergeInto('');
      invalidate();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const flatten = (nodes: unknown[], depth = 0): Array<{ value: string; label: string }> =>
    nodes.flatMap((n) => {
      const node = n as CategoryOption & { children?: unknown[] };
      return [
        { value: node.id, label: `${'— '.repeat(depth)}${node.name}` },
        ...(node.children?.length ? flatten(node.children, depth + 1) : []),
      ];
    });

  return (
    <div className="stack">
      <Notice tone="info" title="Why these wait for approval">
        A user typing a new service name under “Others” creates it here rather than publishing it to everyone
        instantly. Turn that off in <a href="/settings">Settings</a> to restore the literal SRD behaviour.
      </Notice>

      <Card flush>
        <CardHead
          title="Requested categories"
          sub={pending.data?.length ? `${pending.data.length} awaiting a decision` : undefined}
        />
        {pending.isLoading ? (
          <div style={{ padding: 'var(--s5)' }}>
            <div className="skeleton" style={{ height: 56 }} />
          </div>
        ) : !pending.data?.length ? (
          <Empty icon="✅" title="Nothing waiting" body="Every requested category has been decided." />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Sector</th>
                  <th>Path</th>
                  <th>Requested</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {pending.data.map((c) => (
                  <tr key={c.id}>
                    <td className="cell-strong">{c.name}</td>
                    <td>{humanise(c.sector)}</td>
                    <td className="cell-mono">{c.path}</td>
                    <td className="cell-muted">{dateTime(c.createdAt)}</td>
                    <td>
                      <div className="cell-actions">
                        <Button
                          size="sm"
                          variant="success"
                          loading={moderate.isPending && moderate.variables?.id === c.id}
                          onClick={() => moderate.mutate({ id: c.id, status: 'APPROVED' })}
                        >
                          Publish
                        </Button>
                        <Button size="sm" onClick={() => setMerging(c)}>
                          Merge…
                        </Button>
                        <Button size="sm" onClick={() => moderate.mutate({ id: c.id, status: 'REJECTED' })}>
                          Reject
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {merging && (
        <Modal
          title={`Merge “${merging.name}” into an existing category`}
          sub="Any listings and providers already filed under it move across. This cannot be undone from here."
          onClose={() => setMerging(null)}
          footer={
            <>
              <Button onClick={() => setMerging(null)}>Cancel</Button>
              <Button
                variant="primary"
                loading={merge.isPending}
                disabled={!mergeInto}
                onClick={() => merge.mutate({ id: merging.id, intoId: mergeInto })}
              >
                Merge
              </Button>
            </>
          }
        >
          <Field label="Surviving category">
            <Select
              value={mergeInto}
              placeholder="Choose a category…"
              onChange={(e) => setMergeInto(e.target.value)}
              options={all.data ? flatten(all.data).filter((o) => o.value !== merging.id) : []}
            />
          </Field>
        </Modal>
      )}
    </div>
  );
}
